import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Building2, ArrowRight, ArrowLeft } from 'lucide-react';

export const UserTypeSelectionPage: React.FC = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 py-8 md:py-4 h-screen overflow-y-auto">
            {/* Back Button */}
            <div className="w-full max-w-4xl mb-4 md:mb-6">
                <motion.button
                    onClick={() => navigate('/')}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors group"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-sm md:text-base font-medium">Back to Home</span>
                </motion.button>
            </div>

            <div className="w-full max-w-4xl">
                <div className="text-center mb-6 md:mb-12">
                    <motion.div
                        className="flex items-center justify-center space-x-2 md:space-x-3 mb-4 md:mb-6"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <img src="/JobVitalLogo.png" alt="JobVital" className="h-8 md:h-12 w-auto" />
                        <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                            JobVital
                        </span>
                    </motion.div>
                    <motion.h1
                        className="text-2xl md:text-4xl font-bold text-slate-900 mb-2 md:mb-4"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        Join JobVital
                    </motion.h1>
                    <motion.p
                        className="text-sm md:text-xl text-slate-600"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        Choose how you want to use our platform
                    </motion.p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                    {/* Job Seeker Card */}
                    <motion.button
                        onClick={() => navigate('/signup')}
                        className="group relative bg-white p-6 md:p-8 rounded-2xl shadow-sm hover:shadow-xl border-2 border-transparent hover:border-blue-500 transition-all duration-300 text-left"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                            <User className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
                        </div>
                        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2 md:mb-3">I am seeking jobs</h2>
                        <p className="text-sm md:text-base text-slate-600 mb-4 md:mb-6">
                            Create a profile, upload your CV, and get matched with the best opportunities.
                        </p>
                        <div className="flex items-center text-blue-600 text-sm md:text-base font-semibold group-hover:translate-x-2 transition-transform duration-300">
                            Continue
                            <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2" />
                        </div>
                    </motion.button>

                    {/* Hiring Card */}
                    <motion.button
                        onClick={() => navigate('/hiring-signup')}
                        className="group relative bg-white p-6 md:p-8 rounded-2xl shadow-sm hover:shadow-xl border-2 border-transparent hover:border-slate-900 transition-all duration-300 text-left"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                    >
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                            <Building2 className="w-6 h-6 md:w-8 md:h-8 text-slate-900" />
                        </div>
                        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2 md:mb-3">I am hiring</h2>
                        <p className="text-sm md:text-base text-slate-600 mb-4 md:mb-6">
                            Post jobs, find top talent, and manage your recruitment pipeline efficiently.
                        </p>
                        <div className="flex items-center text-slate-900 text-sm md:text-base font-semibold group-hover:translate-x-2 transition-transform duration-300">
                            Continue
                            <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2" />
                        </div>
                    </motion.button>
                </div>

                {/* Login Link */}
                <motion.div
                    className="text-center mt-6 md:mt-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                >
                    <p className="text-sm md:text-base text-slate-600">
                        Already have an account?{' '}
                        <button
                            onClick={() => navigate('/login-selection')}
                            className="text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                        >
                            Sign in
                        </button>
                    </p>
                </motion.div>
            </div>
        </div>
    );
};
